import React from "react";

import { createSlice, current } from "@reduxjs/toolkit";
import { api } from "../../api/httpclient";
import {
  canCreateDataset,
  canEditDataset,
  canDeleteDataset,
  canViewAnyDataset,
} from "../../routing/authorizationhandlers";
import { addNotification } from "./notificationsslice";
import { notificationDurationMiddle } from "../../lib/utils";

const defaultFormInitialValues = {
  Id: 0,
  Title: "",
  Description: "",
  IsAlertBase: false,
  DatasetFields: [],
  SourceType: "",
  RecurrsEvery: 0,
  Corrections: [],
  DatasetLookupTables: [],

  //DB
  DbConnection: {},

  //Consumption
  Consumption: {},

  entityFilter: "",
  entitySysFilter: "",

  //Jobs
  JobCollection: [],
};

const initialState = {
  pageLoaded: false,
  pageDisposed: true,
  processing: false,
  processingText: "",
  mode: 0,
  canView: false,
  canCreate: false,
  canEdit: false,
  canDelete: false,

  entities: [],

  formKey: new Date().toString(),
  formInitialValues: { ...defaultFormInitialValues },
  formValues: { ...defaultFormInitialValues },
  formClientErrors: {},
  formServerErrors: [],

  toolsVisible: true,
  panes: [
    {
      size: "20%",
      min: "20%",
      max: "25%",
      collapsible: false,
    },
    {
      min: "80%",
      max: "80%",
      collapsible: false,
    },
  ],
};

const datasetViewPageSlice = createSlice({
  name: "datasetViewPage",
  initialState: initialState,
  reducers: {
    setInitialState: (state, action) => ({ ...initialState }),

    setProcessing: (state, action) => {
      const { processing = true, processingText = "" } = action.payload;

      return {
        ...state,
        processing: processing,
        processingText: processing == false ? "" : processingText,
      };
    },

    setPanes: (state, action) => ({ ...state, panes: { ...action.payload } }),

    setEntityFilter: (state, action) => ({
      ...state,
      entityFilter: action.payload,
    }),
    setEntitySysFilter: (state, action) => ({
      ...state,
      entitySysFilter: action.payload,
    }),
    setPageValue: (state, action) => ({
      ...state,
      [action.payload.id]: action.payload.value,
    }),

    setFormValue: (state, action) => {
      //payload: {id: fieldId, value: fieldValue}
      let id = action.payload.id;
      let value = action.payload.value;

      return {
        ...state,
        formValues: { ...state.formValues, [id]: value },
      };
    }, //payload: {id: , value:}

    setConnectionFormValue: (state, action) => {
      let id = action.payload.id;
      let value = action.payload.value;

      return {
        ...state,
        formValues: {
          ...state.formValues,
          DbConnection: {
            ...state.formValues.DbConnection,
            [id]: value,
          },
        },
      };
    },

    setFormClientErrors: (state, action) => ({
      ...state,
      formClientErrors: { ...action.payload },
    }),

    setFormServerErrors: (state, action) => ({
      ...state,
      formServerErrors: [...action.payload],
    }),

    pageLoaded: (state, action) => {
      const pageState = current(state);

      if (pageState.pageDisposed) return state;

      return {
        ...state,
        pageLoaded: true,
        processing: false,
        processingText: "",
        //mode: 0,
        //formKey: new Date().toString(),
        //formValues: { ...defaultFormInitialValues },
        //formInitialValues: { ...defaultFormInitialValues },
        formClientErrors: {},
        formServerErrors: [],

        entities: action.payload.Items,

        canView: canViewAnyDataset(action.payload.currentUser),
        canCreate: canCreateDataset(action.payload.currentUser),
        canEdit: canEditDataset(action.payload.currentUser),
        canDelete: canDeleteDataset(action.payload.currentUser),
      };
    },

    entityLoaded: (state, action) => {
      const pageState = current(state);

      if (pageState.pageDisposed) return state;

      return {
        ...state,
        pageLoaded: true,
        processing: false,
        processingText: "",
        mode: 2,
        formKey: new Date().toString(),
        formInitialValues: {
          ...action.payload.data,
          Id: action.payload.Id,
          Title: action.payload.data.Title,
          Description: action.payload.data.Description,
          IsAlertBase: action.payload.data.IsAlertBase,
          DatasetFields: action.payload.data.Fields?.map((field) => ({
            ...field,
            TextMaxLength: field.TextLength,
          })),
          DbConnection: { ...action.payload.data.DbConnection },
          RecurrsEvery: action.payload.data.RecurrsEvery,
          SourceType: action.payload.data.SourceType,
          Consumption: action.payload.data.Consumption,
          Corrections: action.payload.data.Corrections,
        },
        formValues: {
          ...action.payload.data,
          Id: action.payload.Id,
          Title: action.payload.data.Title,
          Description: action.payload.data.Description,
          IsAlertBase: action.payload.data.IsAlertBase,
          DatasetFields: action.payload.data.Fields?.map((field) => ({
            ...field,
            TextMaxLength: field.TextLength,
          })),
          DbConnection: { ...action.payload.data.DbConnection },
          RecurrsEvery: action.payload.data.RecurrsEvery,
          SourceType: action.payload.data.SourceType,
          Consumption: action.payload.data.Consumption,
          Corrections: action.payload.data.Corrections,
        },
        formClientErrors: {},
        formServerErrors: [],
      };
    },

    entityUpdated: (state, action) => {
      const pageState = current(state);

      if (pageState.pageDisposed) return state;

      const entity = pageState.formValues;

      //reflect on entities.name

      return {
        ...state,
        formKey: new Date().toString(),
        formInitialValues: { ...state.formValues },
        formClientErrors: {},
        formServerErrors: [],
        // entities: state.entities.map(e => {
        //     if (e.Id == entity.Id)
        //         return {...e, Title: entity.Title};

        //     return e;
        // })
      };
    },

    entityDeleted: (state, action) => {
      //payload: the entity referenceKey
      let pageState = current(state);

      if (pageState.pageDisposed) return state;
      let entityReferenceKey = action.payload;
      let newItems = pageState.entities.filter(
        (e) => e.Id != entityReferenceKey,
      );

      return {
        ...state,
        entities: newItems,
      };
    },

    removeEntityFromUI: (state, action) => {
      //payload: the entity id
      let pageState = current(state);
      let entityId = action.payload;

      //remove from entities
      let entities = pageState.entities.filter((e) => e.Id != entityId);

      //if it's the current entity => goto no mode
      let currentMode = {};
      if (pageState.mode == 2 && pageState.formValues.Id == entityId)
        currentMode = {
          mode: 0,
          formKey: new Date().toString(),
          formInitialValues: { ...defaultFormInitialValues },
          formValues: { ...defaultFormInitialValues },
          formClientErrors: {},
          formServerErrors: [],
        };

      return { ...state, entities: entities, ...currentMode };
    },

    setPageJobColelction: (state, action) => {
      let pageState = current(state);
      return {
        ...state,
        pageLoaded: true,
        processing: false,
        processingText: "",
        formValues: {
          ...state.formValues,
          JobCollection: action.payload,
        },
      };
    },

    setFieldProperty(state, action) {
      const { id, prop, value } = action.payload;
      return {
        ...state,
        formValues: {
          ...state.formValues,
          DatasetFields: state.formValues.DatasetFields.map((field) =>
            field.Id === id ? { ...field, [prop]: value } : field,
          ),
        },
      };
    },
    resetLookupTable(state, action) {
      console.log("resetLookupTable", action.payload);
      return {
        ...state,
        formValues: {
          ...state.formValues,
          DatasetFields: state.formValues.DatasetFields.map((field) =>
            field.Id === action.payload
              ? { ...field, LookupTableId: "0", LookupTableTitle: "" }
              : field,
          ),
        },
      };
    },

    disposePage: (state, action) => {
      //payload: bool
      if (action.payload) return { ...initialState };
      else return { ...state, pageDisposed: false };
    },
  },
});

export default datasetViewPageSlice.reducer;

export const loadPage = (t) => async (dispatch, getState) => {
  //start with initial values
  dispatch(setInitialState());

  //undispose page
  dispatch(disposePage(false));

  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingPage"),
    }),
  );

  try {
    var response = await api({
      url: "dataset/getuserdefinedwithsystemdatasetlist",
      method: "post",
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().datasetViewPage.pageDisposed) return;

    //it's ok to dispatch here
    let data = response.data.Data;

    dispatch(pageLoaded(data));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const loadEntity = (id, t) => async (dispatch, getState) => {
  //start with initial values
  dispatch(setInitialState());

  //undispose page
  dispatch(disposePage(false));

  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingPage"),
    }),
  );

  try {
    var response = await api({
      url: "/dataset/getuserdefineddatasetbyid",
      method: "post",
      data: {
        id: id,
      },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().datasetViewPage.pageDisposed) return;

    //it's ok to dispatch here
    let data = response.data.Data;

    //transform the response
    dispatch(entityLoaded({ data: data, currentUser: getState().currentUser }));

    //console.log("AFTER API STATE IS ", getState().reportingViewPage)
    return Promise.resolve({
      response: response,
      pageState: getState().datasetViewPage,
    });
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const loadSystemEntity = (id, t) => async (dispatch, getState) => {
  //start with initial values
  dispatch(setInitialState());

  //undispose page
  dispatch(disposePage(false));

  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingPage"),
    }),
  );

  try {
    var response = await api({
      url: "/dataset/getuserdefineddatasetbyid",
      method: "post",
      data: {
        id: id,
      },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().datasetViewPage.pageDisposed) return;

    //it's ok to dispatch here
    let data = response.data.Data;

    //transform the response
    dispatch(entityLoaded({ data: data, currentUser: getState().currentUser }));

    //console.log("AFTER API STATE IS ", getState().reportingViewPage)
    return Promise.resolve({
      response: response,
      pageState: getState().datasetViewPage,
    });
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const saveEntity = (t) => async (dispatch, getState) => {
  const pageState = getState().datasetViewPage;

  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.updatingDataset"),
    }),
  );

  try {
    var response = await api({
      url: "/dataset/updateuserdefineddataset",
      method: "post",
      data: {
        id: pageState.formValues.Id,
        title: pageState.formValues.Title,
        dataBaseTypeId: pageState.formValues.DbConnection.DataBaseTypeId,
        server: pageState.formValues.DbConnection.Server,
        dataBase: pageState.formValues.DbConnection.DataBase,
        username: pageState.formValues.DbConnection.Username,
        password: pageState.formValues.DbConnection.Password,
        recurrsEvery: pageState.formValues.RecurrsEvery,
      },
      //headers:{'h':'1'}
    });
    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().datasetViewPage.pageDisposed) return;

    let data = response.data.Data;

    dispatch(entityUpdated());

    let successMessage = t("slices.updatedDataset");

    dispatch(
      addNotification({
        text: successMessage,
        type: "success",
        closable: true,
        duration: notificationDurationMiddle,
        position: "topRight",
      }),
    );

    dispatch(setProcessing({ processing: false }));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const testConnection = (t) => async (dispatch, getState) => {
  const pageState = getState().datasetViewPage;

  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.updatingDataset"),
    }),
  );

  try {
    var response = await api({
      url: "/dataset/testdbconnection",
      method: "post",
      data: {
        dataBaseTypeId: pageState.formValues.DbConnection.DataBaseTypeId,
        server: pageState.formValues.DbConnection.Server,
        dataBase: pageState.formValues.DbConnection.DataBase,
        username: pageState.formValues.DbConnection.Username,
        password: pageState.formValues.DbConnection.Password,
      },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().datasetViewPage.pageDisposed) return;

    let data = response.data.Data;

    if (data.Success) {
      dispatch(
        addNotification({
          text: t("slices.testedConnection"),
          type: "success",
          closable: true,
          duration: notificationDurationMiddle,
          position: "topRight",
        }),
      );
    } else {
      dispatch(
        addNotification({
          text: t("slices.failedConnection"),
          type: "error",
          closable: true,
          duration: notificationDurationMiddle,
          position: "topRight",
        }),
      );
    }

    dispatch(setProcessing({ processing: false }));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const deleteEntity = (id, t) => async (dispatch, getState) => {
  //payload the entity id
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.deletingDataset"),
    }),
  );

  try {
    var response = await api({
      url: "/dataset/deleteuserdefineddataset",
      method: "post",
      data: { id: id },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().datasetViewPage.pageDisposed) return;

    let data = response.data.Data;

    dispatch(
      addNotification({
        text: t("slices.deletedDataset"),
        type: "success",
        closable: true,
        duration: 3,
        position: "topRight",
      }),
    );

    dispatch(entityDeleted(id));

    dispatch(setProcessing({ processing: false }));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const getJobCollectionByDatasetId =
  (id, t) => async (dispatch, getState) => {
    const pageState = getState().datasetViewPage;

    //show loader
    dispatch(
      setProcessing({
        processing: true,
        processingText: t("slices.updatingDataset"),
      }),
    );

    try {
      var response = await api({
        url: "/dataset/getdatasetcopyjobcollectionbyid",
        method: "post",
        data: {
          DatasetId: id,
        },
        //headers:{'h':'1'}
      });
      //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
      if (getState().datasetViewPage.pageDisposed) return;

      let data = response.data.Data.CopyJobs;

      dispatch(setPageJobColelction(data));

      dispatch(setProcessing({ processing: false }));

      return Promise.resolve(response);
    } catch (err) {
      //i am here => could be any error included 200 and Succeeded=false
      return Promise.reject(err);
    }
  };

export const saveDatasetFields =
  (datasetId, fields, t) => async (dispatch, getState) => {
    const pageState = getState().datasetViewPage;

    //show loader
    dispatch(
      setProcessing({
        processing: true,
        processingText: t("datasets.datasetview.updatefields"),
      }),
    );

    try {
      let apiUrl = "";
      if (fields[0].Id < 0) {
        apiUrl = "dataset/addfieldstodataset";
      } else {
        apiUrl = "dataset/updatefieldsperdataset";
      }
      var response = await api({
        url: apiUrl,
        method: "post",
        data: {
          DataSetId: datasetId,
          Fields: fields,
        },
      });
      //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
      if (getState().datasetViewPage.pageDisposed) return;

      let data = response.data.Data;

      dispatch(setProcessing({ processing: false, processingText: "" }));

      return Promise.resolve(response);
    } catch (err) {
      //i am here => could be any error included 200 and Succeeded=false
      return Promise.reject(err);
    }
  };

export const deleteDatasetField = (id, t) => async (dispatch, getState) => {
  const pageState = getState().datasetViewPage;

  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: "Updating Dataset Fields...",
    }),
  );

  try {
    var response = await api({
      url: "dataset/deletedatafield",
      method: "post",
      data: {
        Id: id,
      },
    });
    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().datasetViewPage.pageDisposed) return;

    let data = response.data.Data;

    dispatch(setProcessing({ processing: false, processingText: "" }));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const getUserDefinedLookupList = (t) => async (dispatch, getState) => {
  //show loading
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingDataset"),
    }),
  );

  try {
    var response = await api({
      url: "dataset/getuserdefinedlookuplist",
      method: "post",

      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().datasetViewPage.pageDisposed) return;

    let data = response.data.Data;

    let lookupTablesArr = [
      { Id: 0, Title: t("slices.select") },
      { Id: -1, Title: t("slices.new") },
    ];

    data.Lookups.forEach((item) => {
      lookupTablesArr.push({ Id: item.Id, Title: item.Title });
    });

    dispatch(
      setFormValue({ id: "DatasetLookupTables", value: lookupTablesArr }),
    );

    dispatch(setProcessing({ processing: false, processingText: "" }));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const {
  setInitialState,
  setProcessing,
  setPageValue,
  setFormValue,
  setConnectionFormValue,
  setFormClientErrors,
  setFormServerErrors,
  entityDeleted,
  setPanes,
  pageLoaded,
  entityLoaded,
  entityUpdated,
  removeEntityFromUI,
  setEntityFilter,
  setEntitySysFilter,
  setPageJobColelction,
  setFieldProperty,
  resetLookupTable,
  disposePage,
} = datasetViewPageSlice.actions;
